import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { RestoreLocationsMap } from "@/components/site/RestoreLocationsMap";
import { scheduleUrl } from "@/lib/site-data";

import { LOCATION_IMAGES } from "@/assets/restore/locations";
import { REGIONS, STATES, type City } from "@/lib/location-directory";
import { CLINICS } from "@/lib/clinics";
import { PhoneLink } from "@/components/site/PhoneLink";
import { QuoteBand } from "@/components/site/QuoteBand";
import { ServiceAreaCardBody } from "@/components/site/ServiceAreaCard";

const PHONE = "(866) 488-9356";


function mapUrl(address: string, city: string, state: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${address} ${city} ${state}`,
  )}`;
}

const NAME_IMAGE_OVERRIDES: Record<string, string> = {
  "Chicago \u2013 Fulton Market": "fulton-market",
  "Chicago \u2013 Hancock Center": "hancock",
  "Chicago Lincoln Park": "lincoln-park",
};

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Photos are keyed by office slug ("brooklyn-heights", "dallas-plano"). The old
// lookup used the last part of `city.to`, which is empty for "/x/y/" links, so
// almost every card had no photo.
function cityImage(city: City): string | undefined {
  const fromLink = city.to ? city.to.split("/").filter(Boolean).pop() ?? "" : "";
  const keys = [NAME_IMAGE_OVERRIDES[city.name], slugify(city.name), fromLink];
  for (const key of keys) {
    if (key && LOCATION_IMAGES[key]) return LOCATION_IMAGES[key];
  }
  return undefined;
}

export const Route = createFileRoute("/locations/")({
  head: () => ({
    meta: [
      { title: "Hair Transplant Locations Near You | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Find a RESTORE® Hair location near you: consultation offices in Chicago, Dallas, Denver, Charlotte and New York, and free virtual consultations nationwide.",
      },
      { property: "og:title", content: "Hair Transplant Locations Near You | RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Browse RESTORE® Hair locations by state and book a free hair loss consultation, in person in our main markets or virtually from anywhere.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://restorehair.com/locations/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/" }],
  }),
  component: LocationsIndex,
});


// The clinic booking pages (the sitemap's /schedule-a-consult/ entries), then
// the virtual consult.
const BOOKING_PAGES: { label: string; href: string }[] = [
  ...CLINICS.filter((c) => c.page?.startsWith("/schedule-a-consult/")).map((c) => ({
    label: c.name,
    href: c.page as string,
  })),
  { label: "Boulder", href: "/schedule-a-consult/boulder/" },
  { label: "Virtual", href: "/schedule-a-consult/virtual/" },
];

function LocationsIndex() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <h1 className="text-3xl font-extrabold leading-tight text-foreground md:text-4xl">
              <span className="font-extrabold">RESTORE®</span>{" "}
              <span className="font-light">Hair Locations</span>
            </h1>
            {/* Every regional page, so each one has an internal link. */}
            <nav aria-label="Regions" className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm font-semibold">
              {REGIONS.map((r) => (
                <a key={r.href} href={r.href} className="text-foreground underline underline-offset-4 hover:text-primary">
                  {r.label}
                </a>
              ))}
            </nav>
            {/* Every clinic booking page, so each one has a link from here
                (Search Console, 17 Oct 2026: four booking pages discovered
                but not indexed). */}
            <nav
              aria-label="Book at a clinic"
              className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm"
            >
              <span className="text-foreground/70">Book a consultation:</span>
              {BOOKING_PAGES.map((b) => (
                <a key={b.href} href={b.href} className="text-foreground underline underline-offset-4 hover:text-primary">
                  {b.label}
                </a>
              ))}
            </nav>
            <nav className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              {STATES.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  className="text-primary underline underline-offset-4 hover:no-underline"
                >
                  {s.name}
                </a>
              ))}
            </nav>
          </div>
        </section>
        <QuoteBand />

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            {STATES.map((state) => (
              <div key={state.slug} id={state.slug} className="mb-12 scroll-mt-24 last:mb-0">
                <h2 className="border-b-2 border-primary pb-2 text-xl font-extrabold text-foreground md:text-2xl">
                  <Link
                    to={`/locations/${state.slug}/` as string}
                    className="transition-colors hover:text-primary"
                  >
                    {state.name}
                  </Link>
                </h2>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {state.cities.map((city) => (
                    <article
                      key={city.name}
                      className="flex flex-col overflow-hidden border border-border bg-background shadow-sm"
                    >
                      {cityImage(city) ? (
                        city.to ? (
                          <Link to={city.to as string}>
                            <img
                              src={cityImage(city)}
                              alt={`RESTORE Hair ${city.serviceArea ? "serving" : "clinic in"} ${city.name}, ${state.name}`}
                              loading="lazy"
                              className="h-48 w-full object-cover"
                            />
                          </Link>
                        ) : (
                          <img
                            src={cityImage(city)}
                            alt={`RESTORE Hair ${city.serviceArea ? "serving" : "clinic in"} ${city.name}, ${state.name}`}
                            loading="lazy"
                            className="h-48 w-full object-cover"
                          />
                        )
                      ) : null}
                      {city.serviceArea ? (
                        <div className="flex flex-1 flex-col p-6">
                          <ServiceAreaCardBody area={city.serviceArea} headingHref={city.to ?? undefined} />
                        </div>
                      ) : (
                      <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-lg font-bold text-foreground">
                        {city.to ? (
                          <Link to={city.to as string} className="transition-colors hover:text-primary">
                            {city.name}
                          </Link>
                        ) : (
                          city.name
                        )}
                      </h3>
                      <div className="mt-3 text-sm text-foreground/80">
                        <p>{city.address}</p>
                        <p className="mt-1"><PhoneLink phone={PHONE} /></p>
                      </div>
                      <a
                        href={mapUrl(city.address, city.name, state.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 self-start text-sm font-bold text-primary underline underline-offset-4 hover:no-underline"
                      >
                        View Map
                      </a>
                      <a
                        href={scheduleUrl}
                        className="mt-auto inline-block bg-primary px-5 py-3 pt-3 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90 mt-6"
                      >
                        Book a Consultation
                      </a>
                      </div>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground md:text-3xl">
              <span className="font-extrabold">RESTORE Hair</span>{" "}
              <span className="font-light">Transplants</span>
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/80">
              <p>
                Explore the premier locations for hair restoration at RESTORE® Hair, conveniently
                spread across the country. Our expert facilities are dedicated to specializing in
                hair transplant, ensuring that each client receives exceptional care tailored to
                their unique needs. Whether you're looking to restore your hairline or seeking
                solutions to fill in thinning areas, our committed team of hair restoration
                surgeons are here to support you every step of the way. We understand that the
                hair transplant journey to reclaiming your confidence is deeply personal, and we
                are devoted to providing the highest quality of service and results which is why
                we have many hair transplant locations.
              </p>
              <p>
                We specialize in the RESTORE® No-Shave FUE™ hair transplant, a minimally invasive
                hair transplant technique designed for natural-looking results with minimal
                downtime. Our consultation offices are in our five main markets: Chicago, Dallas,
                Denver, Charlotte and New York. Procedures are performed at our surgical offices,
                and virtual consultations are available nationwide.
              </p>
              <p>
                To get started, we invite you to{" "}
                <a
                  href={scheduleUrl}
                  className="font-bold text-primary underline underline-offset-4 hover:no-underline"
                >
                  schedule your free consultation
                </a>{" "}
                today, virtually from the comfort of your own home or in person at one of our
                main locations. Let us help you embark on the transformative path to restoring
                your natural hair at RESTORE, where your satisfaction and renewed confidence are
                our top priorities.
              </p>
            </div>
          </div>
        </section>

        <RestoreLocationsMap hideList />

      </main>
      <Footer />
    </div>
  );
}
